import { useCallback, useEffect, useMemo, useState } from "react";
import { useLessonVoiceContext } from "@/providers/LessonVoiceProvider";
import { lessonVoice } from "@/lib/audio/lessonVoiceController";
import {
  getLumiHolocardMoment,
  type LumiHolocardVoiceMoment,
} from "@/lib/audio/lumiHolocardVoiceMap";
import { recordLearningEvent } from "@/lib/learning-engine/eventLogger";

export type LumiHolocardStatus =
  | "idle"
  | "preparing"
  | "speaking"
  | "unavailable"
  | "failed";

/**
 * Click-to-play Lumi holocard hook.
 *
 * Reuses the lesson voice controller so only one voice can play at a
 * time (auto lesson voice moments, module voice moments, and Lumi
 * holocard voice moments cannot stack). Never auto-plays — the learner
 * must intentionally activate the holocard.
 */
export function useLumiHolocard(momentId: string | null | undefined) {
  const { playMoment, speakingMomentId, enabled } = useLessonVoiceContext();
  const [status, setStatus] = useState<LumiHolocardStatus>("idle");

  const moment: LumiHolocardVoiceMoment | undefined = useMemo(
    () => (momentId ? getLumiHolocardMoment(momentId) : undefined),
    [momentId],
  );

  useEffect(() => {
    if (!momentId) return;
    if (speakingMomentId === momentId) setStatus("speaking");
    else if (status === "speaking") setStatus("idle");
  }, [speakingMomentId, momentId, status]);

  const play = useCallback(async () => {
    if (!moment) return;
    // Adaptive Learning Engine: log the learner-initiated open of the Lumi
    // holocard the moment the button is tapped, regardless of whether
    // voice audio is available. This is a support signal, not a
    // completion signal — it never unlocks or rewards.
    void recordLearningEvent({
      eventType: "lumi_holocard_opened",
      metadata: { momentId: moment.momentId },
    });
    // The holocard is a learner-initiated action — always allow a fresh
    // attempt on click, even if a previous attempt was silently marked
    // "already_played" or failed transiently.
    lessonVoice.resetMoments([moment.momentId]);
    setStatus("preparing");
    const result = await playMoment(moment.momentId);
    switch (result.status) {
      case "played":
        void recordLearningEvent({
          eventType: "lumi_support_used",
          metadata: { momentId: moment.momentId, backendStatus: result.backendStatus },
        });
        return;
      case "already_played":
      case "suppressed":
        setStatus("idle");
        return;
      case "queued_autoplay":
        setStatus("preparing");
        return;
      case "generating_timeout":
      case "unavailable":
      case "unknown_moment":
        setStatus("unavailable");
        return;
      case "failed":
      default:
        setStatus("failed");
    }
  }, [moment, playMoment]);

  return { moment, status, play, voiceEnabled: enabled };
}